import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Video, 
  Mic, 
  MicOff, 
  Monitor, 
  Camera, 
  Circle, 
  Square, 
  Pause, 
  Play,
  Settings,
  Download,
  Upload,
  Clock,
  Volume2,
  CheckCircle,
  X,
  Maximize,
  Move,
  RotateCcw,
  ZoomIn,
  MousePointer,
  Sparkles,
  Focus,
  Loader2,
  Image
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { useMediaPipeSegmentation, useVideoEffectsProcessor } from '../hooks/useAdvancedVideoProcessing';
import { generateStudioBackgroundDataUrl, STUDIO_BACKGROUNDS } from '../components/ValnexoStudioBackground';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_screen-studio-3/artifacts/rr2m67nx_logo%20valnexo%20vectorise%CC%81.png";

const StudioPage = () => {
  const { api, token } = useAuth();
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  
  // Media streams
  const [screenStream, setScreenStream] = useState(null);
  const [webcamStream, setWebcamStream] = useState(null);
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');
  
  // Settings
  const [showWebcam, setShowWebcam] = useState(true);
  const [webcamShape, setWebcamShape] = useState('round'); // round, rect
  const [webcamSize, setWebcamSize] = useState(150);
  const [webcamPosition, setWebcamPosition] = useState({ x: 20, y: 20 });
  const [audioLevel, setAudioLevel] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  
  // Background removal settings
  const [backgroundRemoval, setBackgroundRemoval] = useState(false);
  const [backgroundType, setBackgroundType] = useState('studio'); // studio, blur, color, image
  const [backgroundColor, setBackgroundColor] = useState('#0F172A');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('valnexo-studio');
  
  // Generate studio background URL
  const studioBackgroundUrl = generateStudioBackgroundDataUrl(1280, 720);
  
  // Get actual background image URL based on type
  const getBackgroundImageUrl = () => {
    if (backgroundType === 'studio') {
      return studioBackgroundUrl;
    } else if (backgroundType === 'image' && backgroundImage) {
      return backgroundImage;
    }
    return '';
  };
  
  // MediaPipe Segmentation for real background removal
  const { 
    canvasRef: segmentationCanvasRef,
    outputCanvasRef: segmentationOutputRef, 
    isReady: segmentationReady,
    isProcessing: segmentationProcessing,
    error: segmentationError,
    startProcessing: startSegmentation, 
    stopProcessing: stopSegmentation,
    getProcessedStream 
  } = useMediaPipeSegmentation({
    enabled: backgroundRemoval,
    backgroundType: backgroundType === 'studio' ? 'image' : backgroundType,
    backgroundColor,
    backgroundImageUrl: getBackgroundImageUrl(),
    blurAmount: 12
  });
  
  // Video effects processor
  const { 
    effects, 
    toggleZoom, 
    setZoomLevel,
    toggleZoomFollowMouse,
    toggleCursorHighlight, 
    setCursorHighlightSize,
    toggleClickEffect,
    toggleSpotlight,
    applyEffects 
  } = useVideoEffectsProcessor();
  
  // Upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoTitle, setVideoTitle] = useState('');
  const [generateSubtitles, setGenerateSubtitles] = useState(true);
  const [subtitleLanguage, setSubtitleLanguage] = useState('fr');
  const [generatingSubtitles, setGeneratingSubtitles] = useState(false);
  
  // Video branding settings
  const [showBranding, setShowBranding] = useState(true);
  const [brandingPosition, setBrandingPosition] = useState('bottom-right'); // top-left, top-right, bottom-left, bottom-right
  const [brandingOpacity, setBrandingOpacity] = useState(80);
  const [customLogo, setCustomLogo] = useState(null); // Custom uploaded logo
  const [customLogoPreview, setCustomLogoPreview] = useState(LOGO_URL); // Default to Valnexo logo
  const logoInputRef = useRef(null);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const screenPreviewRef = useRef(null);
  const webcamPreviewRef = useRef(null);
  const processedWebcamRef = useRef(null);
  const effectsCanvasRef = useRef(null);
  const screenBoundsRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const effectsAnimationRef = useRef(null);

  // Detect Safari browser
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Get media devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        // Request permission first with Safari-compatible constraints
        const constraints = {
          audio: true,
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };
        
        const tempStream = await navigator.mediaDevices.getUserMedia(constraints);
        // Stop the temp stream immediately after getting permissions
        tempStream.getTracks().forEach(track => track.stop());
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        setAudioDevices(devices.filter(d => d.kind === 'audioinput'));
        setVideoDevices(devices.filter(d => d.kind === 'videoinput'));
        
        const defaultAudio = devices.find(d => d.kind === 'audioinput');
        const defaultVideo = devices.find(d => d.kind === 'videoinput');
        
        if (defaultAudio) setSelectedAudioDevice(defaultAudio.deviceId);
        if (defaultVideo) setSelectedVideoDevice(defaultVideo.deviceId);
      } catch (error) {
        console.error('Error getting devices:', error);
        if (error.name === 'NotAllowedError') {
          toast.error('Veuillez autoriser l\'accès à la caméra et au micro dans les paramètres de votre navigateur');
        } else if (error.name === 'NotFoundError') {
          toast.error('Aucune caméra ou micro détecté');
        } else {
          toast.error('Impossible d\'accéder aux périphériques: ' + error.message);
        }
      }
    };
    getDevices();

    return () => {
      stopAllStreams();
    };
  }, []);

  // Start webcam preview
  useEffect(() => {
    if (selectedVideoDevice && showWebcam) {
      startWebcam();
    }
    return () => {
      if (webcamStream) {
        webcamStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [selectedVideoDevice, showWebcam]);

  // Audio level monitoring
  useEffect(() => {
    if (webcamStream && !isMuted) {
      setupAudioAnalyser(webcamStream);
    }
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [webcamStream, isMuted]);

  const setupAudioAnalyser = (stream) => {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      const updateLevel = () => {
        if (!analyserRef.current) return;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average / 255 * 100);
        requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (error) {
      console.error('Audio analyser error:', error);
    }
  };

  const startWebcam = async () => {
    try {
      // Safari-compatible constraints - avoid 'exact' which can fail
      const videoConstraints = selectedVideoDevice 
        ? { deviceId: { ideal: selectedVideoDevice }, width: { ideal: 1280 }, height: { ideal: 720 } }
        : { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' };
      
      const audioConstraints = selectedAudioDevice
        ? { deviceId: { ideal: selectedAudioDevice } }
        : true;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: audioConstraints
      });
      
      setWebcamStream(stream);
      
      if (webcamPreviewRef.current) {
        webcamPreviewRef.current.srcObject = stream;
        
        // Ensure video plays on Safari
        webcamPreviewRef.current.setAttribute('playsinline', 'true');
        webcamPreviewRef.current.setAttribute('autoplay', 'true');
        webcamPreviewRef.current.muted = true;
        
        // Force play for Safari
        const playPromise = webcamPreviewRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(e => {
            console.log('Auto-play prevented, user interaction needed:', e);
          });
        }
        
        // Start background segmentation if enabled
        if (backgroundRemoval) {
          webcamPreviewRef.current.onloadedmetadata = () => {
            startSegmentation(webcamPreviewRef.current);
          };
        }
      }
      
      toast.success('Caméra activée');
    } catch (error) {
      console.error('Webcam error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Accès à la caméra refusé. Vérifiez les permissions dans Safari > Préférences > Sites web > Caméra');
      } else if (error.name === 'NotFoundError') {
        toast.error('Aucune caméra trouvée');
      } else if (error.name === 'NotReadableError') {
        toast.error('La caméra est utilisée par une autre application');
      } else {
        toast.error('Erreur caméra: ' + error.message);
      }
    }
  };

  // Toggle background removal and start/stop segmentation
  const handleBackgroundRemovalToggle = () => {
    const newValue = !backgroundRemoval;
    setBackgroundRemoval(newValue);
    
    if (newValue && webcamPreviewRef.current && webcamStream) {
      startSegmentation(webcamPreviewRef.current);
      toast.success('Suppression du fond activée');
    } else {
      stopSegmentation();
    }
  };

  const stopAllStreams = () => {
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
    }
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      setWebcamStream(null);
    }
  };

  const startScreenCapture = async () => {
    try {
      // Safari-compatible getDisplayMedia constraints
      // Safari doesn't support all options that Chrome does
      const displayMediaOptions = {
        video: {
          cursor: 'always',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        // Safari may not support system audio capture
        audio: !isSafari
      };

      const stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
      setScreenStream(stream);
      
      if (screenPreviewRef.current) {
        screenPreviewRef.current.srcObject = stream;
        
        // Force play for Safari
        screenPreviewRef.current.setAttribute('playsinline', 'true');
        screenPreviewRef.current.setAttribute('autoplay', 'true');
        screenPreviewRef.current.muted = true;
        
        const playPromise = screenPreviewRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(e => {
            console.log('Screen preview auto-play issue:', e);
          });
        }
      }
      
      // Handle when user stops sharing
      stream.getVideoTracks()[0].onended = () => {
        if (isRecording) {
          stopRecording();
        }
        setScreenStream(null);
        toast.info('Partage d\'écran arrêté');
      };
      
      toast.success('Capture d\'écran démarrée');
      return stream;
    } catch (error) {
      console.error('Screen capture error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Capture d\'écran refusée. Sur Mac, allez dans Préférences Système > Confidentialité > Enregistrement de l\'écran et autorisez Safari');
      } else if (error.name === 'NotSupportedError') {
        toast.error('Capture d\'écran non supportée sur ce navigateur');
      } else {
        toast.error('Erreur capture d\'écran: ' + error.message);
      }
      return null;
    }
  };

  const startRecording = async () => {
    try {
      // Get screen stream if not already captured
      let screen = screenStream;
      if (!screen) {
        screen = await startScreenCapture();
        if (!screen) return;
      }

      // Store screen bounds for effects
      if (screenPreviewRef.current) {
        screenBoundsRef.current = screenPreviewRef.current.getBoundingClientRect();
      }

      // Create effects canvas if we have effects enabled
      const hasEffects = effects.zoom.enabled || effects.cursor.highlight || effects.cursor.clickEffect || effects.spotlight.enabled;
      let finalVideoStream = screen;
      
      if (hasEffects) {
        // Create canvas for effects processing
        if (!effectsCanvasRef.current) {
          effectsCanvasRef.current = document.createElement('canvas');
        }
        const effectsCanvas = effectsCanvasRef.current;
        const sourceVideo = screenPreviewRef.current;
        
        if (sourceVideo) {
          effectsCanvas.width = sourceVideo.videoWidth || 1920;
          effectsCanvas.height = sourceVideo.videoHeight || 1080;
          
          // Create source canvas from video
          const sourceCanvas = document.createElement('canvas');
          sourceCanvas.width = effectsCanvas.width;
          sourceCanvas.height = effectsCanvas.height;
          const sourceCtx = sourceCanvas.getContext('2d');
          
          // Animation loop for effects
          const processEffects = () => {
            if (!isRecording) return;
            
            if (sourceVideo.readyState >= 2) {
              sourceCtx.drawImage(sourceVideo, 0, 0);
              applyEffects(sourceCanvas, effectsCanvas, screenBoundsRef.current);
            }
            
            effectsAnimationRef.current = requestAnimationFrame(processEffects);
          };
          
          // Start effects processing
          processEffects();
          
          // Get stream from effects canvas
          try {
            finalVideoStream = effectsCanvas.captureStream(30);
            // Add screen audio if available
            const screenAudioTrack = screen.getAudioTracks()[0];
            if (screenAudioTrack) {
              finalVideoStream.addTrack(screenAudioTrack);
            }
          } catch (err) {
            console.warn('Could not capture effects stream, using original:', err);
            finalVideoStream = screen;
          }
        }
      }

      // Combine streams
      const tracks = [...finalVideoStream.getTracks()];
      
      // Add webcam audio if available and not muted
      if (webcamStream && !isMuted) {
        const audioTrack = webcamStream.getAudioTracks()[0];
        if (audioTrack) {
          tracks.push(audioTrack);
        }
      }

      const combinedStream = new MediaStream(tracks);
      
      // Setup MediaRecorder with Safari-compatible options
      // Safari supports MP4 with H.264, not WebM with VP9
      let mimeType = '';
      let fileExtension = 'webm';
      
      // Try different codecs in order of preference
      const mimeTypes = [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4;codecs=h264',
        'video/mp4'
      ];
      
      for (const type of mimeTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          if (type.includes('mp4')) {
            fileExtension = 'mp4';
          }
          break;
        }
      }
      
      if (!mimeType) {
        toast.error('Aucun format vidéo supporté par votre navigateur');
        return;
      }
      
      console.log('Using MediaRecorder mimeType:', mimeType);
      
      const options = { mimeType };
      
      try {
        mediaRecorderRef.current = new MediaRecorder(combinedStream, options);
      } catch (e) {
        console.warn('MediaRecorder with options failed, trying without:', e);
        mediaRecorderRef.current = new MediaRecorder(combinedStream);
      }
      
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blobType = mimeType || 'video/webm';
        const blob = new Blob(chunksRef.current, { type: blobType });
        setRecordedBlob(blob);
        setShowUploadDialog(true);
        
        // Stop effects animation
        if (effectsAnimationRef.current) {
          cancelAnimationFrame(effectsAnimationRef.current);
        }
      };
      
      mediaRecorderRef.current.start(1000); // Collect data every second
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      
      toast.success('Enregistrement démarré' + (hasEffects ? ' avec effets' : ''));
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Erreur lors du démarrage de l\'enregistrement');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(t => t + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        clearInterval(timerRef.current);
      }
      setIsPaused(!isPaused);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
      setIsPaused(false);
      
      // Stop effects animation
      if (effectsAnimationRef.current) {
        cancelAnimationFrame(effectsAnimationRef.current);
        effectsAnimationRef.current = null;
      }
      
      // Stop screen stream
      if (screenStream) {
        screenStream.getTracks().forEach(track => track.stop());
        setScreenStream(null);
      }
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const downloadRecording = () => {
    if (recordedBlob) {
      const url = URL.createObjectURL(recordedBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recording-${Date.now()}.webm`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const uploadRecording = async () => {
    if (!recordedBlob) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', recordedBlob, `${videoTitle || 'recording'}.webm`);
      
      const response = await api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const mediaId = response.data.id;
      toast.success('Vidéo uploadée avec succès !');
      
      // Generate subtitles if enabled
      if (generateSubtitles && mediaId) {
        setGeneratingSubtitles(true);
        toast.info('Génération des sous-titres en cours...');
        try {
          await api.post(`/media/${mediaId}/transcribe?language=${subtitleLanguage}&format=srt`);
          toast.success('Sous-titres générés avec succès !');
        } catch (subError) {
          console.error('Subtitle generation error:', subError);
          toast.error('Erreur lors de la génération des sous-titres');
        } finally {
          setGeneratingSubtitles(false);
        }
      }
      
      setShowUploadDialog(false);
      setRecordedBlob(null);
      setVideoTitle('');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const discardRecording = () => {
    setRecordedBlob(null);
    setShowUploadDialog(false);
    setVideoTitle('');
  };

  // Handle logo file upload
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image (PNG, JPG, SVG)');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('L\'image ne doit pas dépasser 2MB');
        return;
      }
      
      setCustomLogo(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomLogoPreview(reader.result);
        toast.success('Logo importé avec succès');
      };
      reader.readAsDataURL(file);
    }
  };

  // Reset logo to default
  const resetLogo = () => {
    setCustomLogo(null);
    setCustomLogoPreview(LOGO_URL);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
    toast.info('Logo par défaut restauré');
  };

  return (
    <div className="h-screen bg-slate-900 text-white flex flex-col studio-mode">
      {/* Safari Warning Banner */}
      {isSafari && (
        <div className="bg-amber-500/20 border-b border-amber-500/30 px-4 py-2 text-sm text-amber-200 flex items-center justify-center gap-2">
          <span>⚠️</span>
          <span>Safari a des limitations pour la capture vidéo. Pour une meilleure expérience, utilisez <strong>Chrome</strong> ou <strong>Firefox</strong>.</span>
          <button 
            onClick={() => {
              const banner = document.querySelector('.safari-warning');
              if (banner) banner.style.display = 'none';
            }}
            className="ml-4 text-amber-400 hover:text-amber-300"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Header */}
      <header className="h-16 border-b border-slate-700 flex items-center justify-between px-6 bg-slate-900/80 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.history.back()}
            className="text-slate-400 hover:text-white hover:bg-slate-700"
            data-testid="back-btn"
          >
            <X className="w-5 h-5" />
          </Button>
          
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Valnexo" className="w-8 h-8 object-contain" />
            <span className="font-semibold font-['Outfit']">Studio</span>
          </div>
          
          {isRecording && (
            <div className="flex items-center gap-3 px-4 py-1.5 rounded-full bg-red-500/20 border border-red-500/30">
              <div className={`w-3 h-3 rounded-full bg-red-500 ${isPaused ? '' : 'animate-pulse-recording'}`} />
              <span className="font-mono text-sm">{formatTime(recordingTime)}</span>
              {isPaused && <span className="text-xs text-red-400">PAUSE</span>}
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {/* Audio level indicator */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800">
            <Volume2 className="w-4 h-4 text-slate-400" />
            <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 transition-all duration-100"
                style={{ width: `${audioLevel}%` }}
              />
            </div>
          </div>
          
          <Button
            data-testid="toggle-mute-btn"
            variant="ghost"
            size="icon"
            onClick={() => setIsMuted(!isMuted)}
            className={isMuted ? 'text-red-400' : 'text-slate-300'}
          >
            {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Preview Area */}
        <div className="flex-1 p-6 flex items-center justify-center relative">
          {/* Screen Preview */}
          <div className="relative w-full max-w-5xl aspect-video bg-slate-800 rounded-xl overflow-hidden border border-slate-700">
            {screenStream ? (
              <video
                ref={screenPreviewRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Monitor className="w-16 h-16 text-slate-600 mb-4" />
                <p className="text-slate-400 text-lg mb-4">Aucune capture en cours</p>
                <Button
                  data-testid="start-screen-capture-btn"
                  onClick={startScreenCapture}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  Capturer l'écran
                </Button>
              </div>
            )}
            
            {/* Webcam Bubble */}
            {showWebcam && webcamStream && (
              <div 
                className={`absolute ${webcamShape === 'round' ? 'webcam-bubble' : 'webcam-bubble-rect'}`}
                style={{
                  width: webcamSize,
                  height: webcamShape === 'round' ? webcamSize : webcamSize * 0.75,
                  right: webcamPosition.x,
                  bottom: webcamPosition.y,
                  cursor: 'move'
                }}
              >
                {/* Show processed canvas if background removal is active */}
                {backgroundRemoval && segmentationReady ? (
                  <>
                    <video
                      ref={webcamPreviewRef}
                      autoPlay
                      muted
                      playsInline
                      className="hidden"
                    />
                    <canvas
                      ref={segmentationCanvasRef}
                      className="hidden"
                    />
                    <canvas
                      ref={segmentationOutputRef}
                      className="w-full h-full object-cover transform scale-x-[-1]"
                    />
                  </>
                ) : (
                  <video
                    ref={webcamPreviewRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover transform scale-x-[-1]"
                  />
                )}
                
                {/* Loading indicator for segmentation */}
                {backgroundRemoval && !segmentationReady && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
              </div>
            )}
            
            {/* Branding Logo Overlay */}
            {showBranding && (
              <div 
                className={`absolute p-3 ${
                  brandingPosition === 'top-left' ? 'top-4 left-4' :
                  brandingPosition === 'top-right' ? 'top-4 right-4' :
                  brandingPosition === 'bottom-left' ? 'bottom-4 left-4' :
                  'bottom-4 right-4'
                }`}
                style={{ opacity: brandingOpacity / 100 }}
              >
                <img 
                  src={customLogoPreview} 
                  alt="Logo" 
                  className="w-12 h-12 object-contain drop-shadow-lg"
                />
              </div>
            )}
          </div>
        </div>

        {/* Settings Sidebar */}
        <div className="w-72 border-l border-slate-700 bg-slate-800/50 p-4 space-y-6 overflow-y-auto">
          {/* Webcam Settings */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Caméra
            </h3>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Afficher</span>
              <button
                data-testid="toggle-webcam-btn"
                onClick={() => setShowWebcam(!showWebcam)}
                className={`w-10 h-6 rounded-full transition-colors ${
                  showWebcam ? 'bg-blue-600' : 'bg-slate-600'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                  showWebcam ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </div>
            
            {showWebcam && (
              <>
                <Select value={selectedVideoDevice} onValueChange={setSelectedVideoDevice}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Sélectionner caméra" />
                  </SelectTrigger>
                  <SelectContent>
                    {videoDevices.map(device => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Caméra ${device.deviceId.slice(0, 8)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <div className="space-y-2">
                  <span className="text-sm text-slate-400">Forme</span>
                  <div className="flex gap-2">
                    <button
                      data-testid="webcam-shape-round"
                      onClick={() => setWebcamShape('round')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm transition-colors ${
                        webcamShape === 'round' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      Ronde
                    </button>
                    <button
                      data-testid="webcam-shape-rect"
                      onClick={() => setWebcamShape('rect')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm transition-colors ${
                        webcamShape === 'rect' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      Rectangle
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <span className="text-sm text-slate-400">Taille: {webcamSize}px</span>
                  <Slider
                    value={[webcamSize]}
                    onValueChange={([v]) => setWebcamSize(v)}
                    min={80}
                    max={300}
                    step={10}
                    className="py-2"
                  />
                </div>

                {/* Background Removal - Studio Valnexo */}
                <div className="pt-3 border-t border-slate-700 space-y-3">
                  {/* Prominent Studio Valnexo Button */}
                  <button
                    data-testid="toggle-bg-removal-btn"
                    onClick={handleBackgroundRemovalToggle}
                    className={`w-full p-3 rounded-lg text-left transition-all ${
                      backgroundRemoval 
                        ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white ring-2 ring-blue-400' 
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600 border border-dashed border-slate-500'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center overflow-hidden">
                        {backgroundRemoval ? (
                          <img 
                            src={studioBackgroundUrl} 
                            alt="Studio" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Sparkles className="w-6 h-6 text-slate-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium flex items-center gap-2">
                          Mode Studio Valnexo
                          <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] rounded font-bold">PRO</span>
                        </div>
                        <div className="text-xs opacity-70">
                          {backgroundRemoval ? 'Fond virtuel activé' : 'Cliquez pour activer le fond pro'}
                        </div>
                      </div>
                      {backgroundRemoval && !segmentationReady && (
                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                      )}
                      {backgroundRemoval && segmentationReady && (
                        <CheckCircle className="w-4 h-4 text-emerald-300" />
                      )}
                    </div>
                  </button>
                  
                  {segmentationError && (
                    <p className="text-xs text-red-400">
                      Erreur: {segmentationError}
                    </p>
                  )}
                  
                  {backgroundRemoval && (
                    <>
                      <div className="space-y-2">
                        <span className="text-sm text-slate-400">Type de fond</span>
                        
                        {/* Studio Valnexo - Featured option */}
                        <button
                          onClick={() => setBackgroundType('studio')}
                          className={`w-full p-3 rounded-lg text-left transition-all ${
                            backgroundType === 'studio' 
                              ? 'bg-blue-600 text-white ring-2 ring-blue-400' 
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-8 rounded bg-slate-800 flex items-center justify-center overflow-hidden">
                              <img 
                                src={studioBackgroundUrl} 
                                alt="Studio" 
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div>
                              <div className="text-sm font-medium flex items-center gap-2">
                                Studio Valnexo
                                <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] rounded">PRO</span>
                              </div>
                              <div className="text-xs opacity-70">Tableau + Logo</div>
                            </div>
                          </div>
                        </button>
                        
                        {/* Other options */}
                        <div className="grid grid-cols-3 gap-2">
                          <button
                            onClick={() => setBackgroundType('blur')}
                            className={`py-2 px-2 rounded-lg text-xs transition-colors ${
                              backgroundType === 'blur' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            Flou
                          </button>
                          <button
                            onClick={() => setBackgroundType('color')}
                            className={`py-2 px-2 rounded-lg text-xs transition-colors ${
                              backgroundType === 'color' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            Couleur
                          </button>
                          <button
                            onClick={() => setBackgroundType('image')}
                            className={`py-2 px-2 rounded-lg text-xs transition-colors ${
                              backgroundType === 'image' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            Perso
                          </button>
                        </div>
                      </div>
                      
                      {/* Studio preview */}
                      {backgroundType === 'studio' && (
                        <div className="space-y-2">
                          <span className="text-sm text-slate-400">Aperçu</span>
                          <div className="w-full aspect-video rounded-lg overflow-hidden border border-slate-600">
                            <img 
                              src={studioBackgroundUrl} 
                              alt="Studio Valnexo Preview" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-xs text-emerald-400 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Fond professionnel avec branding
                          </p>
                        </div>
                      )}
                      
                      {backgroundType === 'color' && (
                        <div className="space-y-2">
                          <span className="text-sm text-slate-400">Couleur</span>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={backgroundColor}
                              onChange={(e) => setBackgroundColor(e.target.value)}
                              className="w-10 h-8 rounded border border-slate-600 cursor-pointer"
                            />
                            <Input
                              value={backgroundColor}
                              onChange={(e) => setBackgroundColor(e.target.value)}
                              className="flex-1 bg-slate-700 border-slate-600 text-white text-sm"
                            />
                          </div>
                          {/* Quick color presets */}
                          <div className="flex gap-2">
                            {['#0F172A', '#1F2937', '#18181B', '#1E3A5F', '#14532D'].map(color => (
                              <button
                                key={color}
                                onClick={() => setBackgroundColor(color)}
                                className={`w-8 h-8 rounded-full border-2 transition-all ${
                                  backgroundColor === color ? 'border-white scale-110' : 'border-transparent'
                                }`}
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {backgroundType === 'image' && (
                        <div className="space-y-2">
                          <span className="text-sm text-slate-400">URL de l'image</span>
                          <Input
                            value={backgroundImage}
                            onChange={(e) => setBackgroundImage(e.target.value)}
                            placeholder="https://..."
                            className="bg-slate-700 border-slate-600 text-white text-sm"
                          />
                          {backgroundImage && (
                            <div className="w-full aspect-video rounded-lg overflow-hidden border border-slate-600">
                              <img 
                                src={backgroundImage} 
                                alt="Custom background" 
                                className="w-full h-full object-cover"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      
                      <p className="text-xs text-amber-400">
                        ⚠️ Fonctionnalité en bêta - peut ralentir l'enregistrement
                      </p>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Audio Settings */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Mic className="w-4 h-4" />
              Microphone
            </h3>
            
            <Select value={selectedAudioDevice} onValueChange={setSelectedAudioDevice}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Sélectionner micro" />
              </SelectTrigger>
              <SelectContent>
                {audioDevices.map(device => (
                  <SelectItem key={device.deviceId} value={device.deviceId}>
                    {device.label || `Micro ${device.deviceId.slice(0, 8)}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              data-testid="test-mic-btn"
              variant="outline"
              size="sm"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
              onClick={() => toast.info('Test du microphone...')}
            >
              Tester le micro
            </Button>
          </div>

          {/* Video Effects */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Effets vidéo
            </h3>
            
            {/* Zoom */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Zoom intelligent</span>
              <button
                data-testid="toggle-zoom-btn"
                onClick={() => toggleZoom()}
                className={`w-10 h-6 rounded-full transition-colors ${
                  effects.zoom.enabled ? 'bg-blue-600' : 'bg-slate-600'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                  effects.zoom.enabled ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </div>
            
            {effects.zoom.enabled && (
              <div className="space-y-2">
                <span className="text-sm text-slate-400">Niveau: {effects.zoom.level}x</span>
                <Slider
                  value={[effects.zoom.level]}
                  onValueChange={([v]) => setZoomLevel(v)}
                  min={1}
                  max={4}
                  step={0.5}
                  className="py-2"
                />
              </div>
            )}
            
            {/* Cursor Highlight */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Halo souris</span>
              <button
                data-testid="toggle-cursor-highlight-btn"
                onClick={toggleCursorHighlight}
                className={`w-10 h-6 rounded-full transition-colors ${
                  effects.cursor.highlight ? 'bg-blue-600' : 'bg-slate-600'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                  effects.cursor.highlight ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </div>
            
            {/* Click Effect */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Clics visibles</span>
              <button
                data-testid="toggle-click-effect-btn"
                onClick={toggleClickEffect}
                className={`w-10 h-6 rounded-full transition-colors ${
                  effects.cursor.clickEffect ? 'bg-blue-600' : 'bg-slate-600'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                  effects.cursor.clickEffect ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Cursor Size */}
            {effects.cursor.highlight && (
              <div className="space-y-2">
                <span className="text-sm text-slate-400">Taille halo: {effects.cursor.highlightSize}px</span>
                <Slider
                  value={[effects.cursor.highlightSize]}
                  onValueChange={([v]) => setCursorHighlightSize(v)}
                  min={10}
                  max={60}
                  step={5}
                  className="py-2"
                />
              </div>
            )}

            {/* Spotlight Mode */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Focus className="w-3 h-3 text-slate-500" />
                <span className="text-sm text-slate-400">Mode spotlight</span>
              </div>
              <button
                data-testid="toggle-spotlight-btn"
                onClick={toggleSpotlight}
                className={`w-10 h-6 rounded-full transition-colors ${
                  effects.spotlight.enabled ? 'bg-blue-600' : 'bg-slate-600'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                  effects.spotlight.enabled ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Zoom Follow Mouse */}
            {effects.zoom.enabled && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Zoom suit souris</span>
                <button
                  onClick={toggleZoomFollowMouse}
                  className={`w-10 h-6 rounded-full transition-colors ${
                    effects.zoom.followMouse ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                    effects.zoom.followMouse ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            )}
            
            <p className="text-xs text-emerald-400 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Effets appliqués en temps réel
            </p>
          </div>

          {/* Video Branding */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Image className="w-4 h-4" />
              Branding vidéo
            </h3>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Afficher logo</span>
              <button
                data-testid="toggle-branding-btn"
                onClick={() => setShowBranding(!showBranding)}
                className={`w-10 h-6 rounded-full transition-colors ${
                  showBranding ? 'bg-blue-600' : 'bg-slate-600'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                  showBranding ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </div>
            
            {showBranding && (
              <>
                {/* Logo Upload */}
                <div className="space-y-2">
                  <span className="text-sm text-slate-400">Votre logo</span>
                  <div className="flex gap-2">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      className="flex-1 py-2 px-3 rounded-lg text-xs bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors border border-dashed border-slate-500 flex items-center justify-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {customLogo ? 'Changer le logo' : 'Importer un logo'}
                    </button>
                    {customLogo && (
                      <button
                        onClick={resetLogo}
                        className="py-2 px-3 rounded-lg text-xs bg-slate-700 text-red-400 hover:bg-slate-600 transition-colors"
                        title="Réinitialiser"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">PNG, JPG, SVG (max 2MB)</p>
                </div>
                
                {/* Logo Preview */}
                <div className="p-3 rounded-lg bg-slate-700/50 border border-slate-600">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center overflow-hidden">
                      <img 
                        src={customLogoPreview} 
                        alt="Logo" 
                        className="max-w-full max-h-full object-contain"
                        style={{ opacity: brandingOpacity / 100 }}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-300 font-medium">
                        {customLogo ? customLogo.name : 'Logo Valnexo (par défaut)'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {customLogo ? `${(customLogo.size / 1024).toFixed(1)} KB` : 'Logo par défaut'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-slate-400">Position</span>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'top-left', label: '↖ Haut gauche' },
                      { value: 'top-right', label: '↗ Haut droite' },
                      { value: 'bottom-left', label: '↙ Bas gauche' },
                      { value: 'bottom-right', label: '↘ Bas droite' }
                    ].map((pos) => (
                      <button
                        key={pos.value}
                        onClick={() => setBrandingPosition(pos.value)}
                        className={`py-1.5 px-2 rounded text-xs transition-colors ${
                          brandingPosition === pos.value 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {pos.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <span className="text-sm text-slate-400">Opacité: {brandingOpacity}%</span>
                  <Slider
                    value={[brandingOpacity]}
                    onValueChange={([v]) => setBrandingOpacity(v)}
                    min={20}
                    max={100}
                    step={10}
                    className="py-2"
                  />
                </div>
                
                <p className="text-xs text-emerald-400 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Logo inclus dans l'export vidéo
                </p>
              </>
            )}
          </div>

          {/* Recording Info */}
          {isRecording && (
            <div className="p-4 rounded-lg bg-slate-700/50 border border-slate-600">
              <div className="flex items-center gap-2 text-sm text-slate-300 mb-2">
                <Clock className="w-4 h-4" />
                Durée: {formatTime(recordingTime)}
              </div>
              <div className="text-xs text-slate-500">
                Format: WebM 1080p
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls Footer */}
      <footer className="h-20 border-t border-slate-700 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center gap-4 px-6">
        {!isRecording ? (
          <Button
            data-testid="start-recording-btn"
            onClick={startRecording}
            className="bg-red-600 hover:bg-red-700 px-8 py-6 text-lg"
            disabled={!screenStream}
          >
            <Circle className="w-5 h-5 mr-2 fill-current" />
            Enregistrer
          </Button>
        ) : (
          <>
            <Button
              data-testid="pause-recording-btn"
              onClick={pauseRecording}
              variant="outline"
              className="border-slate-600 text-white hover:bg-slate-700 px-6 py-5"
            >
              {isPaused ? (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Reprendre
                </>
              ) : (
                <>
                  <Pause className="w-5 h-5 mr-2" />
                  Pause
                </>
              )}
            </Button>
            <Button
              data-testid="stop-recording-btn"
              onClick={stopRecording}
              className="bg-red-600 hover:bg-red-700 px-8 py-5"
            >
              <Square className="w-5 h-5 mr-2 fill-current" />
              Arrêter
            </Button>
          </>
        )}
      </footer>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Enregistrement terminé</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {recordedBlob && (
              <video
                src={URL.createObjectURL(recordedBlob)}
                controls
                className="w-full rounded-lg"
              />
            )}
            
            <div className="space-y-2">
              <Label htmlFor="video-title" className="text-slate-300">
                Titre de la vidéo
              </Label>
              <Input
                id="video-title"
                data-testid="video-title-input"
                value={videoTitle}
                onChange={(e) => setVideoTitle(e.target.value)}
                placeholder="Ma vidéo de formation"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            {/* Subtitles Options */}
            <div className="p-4 bg-slate-700/50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-slate-200">Sous-titres automatiques</span>
                  <p className="text-xs text-slate-400">Générés par IA (OpenAI Whisper)</p>
                </div>
                <button
                  data-testid="toggle-subtitles-btn"
                  onClick={() => setGenerateSubtitles(!generateSubtitles)}
                  className={`w-10 h-6 rounded-full transition-colors ${
                    generateSubtitles ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                    generateSubtitles ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              {generateSubtitles && (
                <div className="space-y-2">
                  <Label className="text-slate-400 text-sm">Langue de la vidéo</Label>
                  <Select value={subtitleLanguage} onValueChange={setSubtitleLanguage}>
                    <SelectTrigger className="bg-slate-600 border-slate-500 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="de">Deutsch</SelectItem>
                      <SelectItem value="it">Italiano</SelectItem>
                      <SelectItem value="pt">Português</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              data-testid="download-recording-btn"
              variant="outline"
              onClick={downloadRecording}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
            <Button
              data-testid="discard-recording-btn"
              variant="ghost"
              onClick={discardRecording}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button
              data-testid="upload-recording-btn"
              onClick={uploadRecording}
              disabled={uploading || generatingSubtitles}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {uploading || generatingSubtitles ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudioPage;
